import {
  forwardRef,
  useRef,
  useImperativeHandle,
  useEffect,
  SyntheticEvent,
  FocusEvent,
  KeyboardEvent,
  ChangeEvent,
  ReactNode,
} from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import cn from 'classnames';

import { ErknmFillInProcessMessage } from 'KnmErknmPackage/MainModule/components/ErknmFillInProcessMessage';
import { IInspectionResult } from 'APIPackage/CatalogsModule/interfaces/IInspectionResult';
import { InspectionResultActionTypeEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultActionTypeEnum';
import { ErknmInspectionResultMessage } from 'KnmErknmPackage/MainModule/components/ErknmInspectionResultMessageMessage';

import { TextEditor } from './TextEditor';
import styles from './Textarea.module.scss';

export interface ITextareaProps {
  className?: string;
  inputClassName?: string;
  id?: string;
  name?: string;
  value?: string;
  placeholder?: ReactNode;
  label?: string;
  size?: 'small' | 'medium';
  resize?: 'none' | 'both' | 'horizontal' | 'vertical';
  autoHeight?: boolean;
  autoFocus?: boolean;
  clearable?: boolean;
  invalid?: boolean;
  disabled?: boolean;
  withWarning?: boolean;
  error?: string;
  warning?: string;
  withEditor?: boolean;
  triggerCalculateHeight?: boolean;
  onInput?: (e: SyntheticEvent) => void;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: FocusEvent<HTMLTextAreaElement>) => void;
  onFocus?: (e: FocusEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: KeyboardEvent) => void;
  onClear?: () => void;
  inspectionResult?: IInspectionResult;
}

export const Textarea = forwardRef(
  (
    {
      className = '',
      inputClassName = '',
      value,
      label,
      size,
      resize = 'horizontal',
      autoHeight = true,
      clearable,
      invalid,
      disabled,
      error,
      warning,
      id,
      withWarning,
      withEditor,
      triggerCalculateHeight,
      onClear,
      onChange,
      inspectionResult,
      placeholder,
      ...rest
    }: ITextareaProps,
    ref,
  ) => {
    const classNames = cn([
      styles.Textarea,
      styles[`Resize${resize.charAt(0).toUpperCase()}${resize.slice(1)}`],
      className,
      {
        [styles.TextareaInvalid]: Boolean(error),
        [styles.TextareaDisabled]: disabled,
        [styles.TextareaSizeSmall]: size === 'small',
        [styles.TextareaSizeMedium]: size === 'medium',
        [styles.TextareaAutoHeight]: Boolean(autoHeight),
        [styles.TextareaDisabledInspectionResultYellow]:
          inspectionResult && inspectionResult.resultActionType === InspectionResultActionTypeEnum.LIGHT_YELLOW,
        [styles.TextareaDisabledInspectionResultRed]:
          inspectionResult && inspectionResult.resultActionType === InspectionResultActionTypeEnum.LIGHT_RED,
      },
    ]);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => inputRef.current);
    const inputRefCurrent = inputRef.current;
    const inputRefClearButton = useRef<HTMLButtonElement>(null);
    const inputRefClearButtonCurrent = inputRefClearButton.current;

    const autoHeightTimer = useRef();

    useEffect(() => {
      const handleAutoHeight = () => {
        if (autoHeight && inputRefCurrent) {
          const { style } = inputRefCurrent;

          const { scrollHeight } = inputRefCurrent;

          if (scrollHeight > 0) {
            style.height = '';
            style.height = `${scrollHeight / 16 + 2 / 16}rem`;
            clearTimeout(autoHeightTimer.current);
          } else {
            // @ts-ignore
            autoHeightTimer.current = setTimeout(() => {
              handleAutoHeight();
            }, 2000);
          }
        }
      };

      handleAutoHeight();
    }, [autoHeight, inputRefCurrent, value, triggerCalculateHeight, disabled]);

    useEffect(() => {
      return () => clearTimeout(autoHeightTimer.current);
    }, []);

    const inputClassNames = cn(styles.Input, inputClassName, {
      [styles.WithWarning]: withWarning,
    });

    useEffect(() => {
      if (inputRefCurrent && clearable && inputRefClearButtonCurrent) {
        new ResizeObserver(() => {
          inputRefClearButtonCurrent.style.left = `${inputRefCurrent.offsetWidth - 25}px`;
        }).observe(inputRefCurrent);
      }
    }, [inputRefCurrent, clearable, inputRefClearButtonCurrent]);

    return (
      <div className={classNames}>
        {label && (
          <label className={styles.Label} htmlFor={id}>
            {label}
          </label>
        )}
        {withEditor ? (
          <TextEditor value={value} error={error} onChange={disabled ? undefined : onChange} {...rest} />
        ) : (
          <div className={styles.TextareaContainer}>
            <textarea
              ref={inputRef}
              id={id}
              className={inputClassNames}
              value={value}
              disabled={disabled}
              onChange={disabled ? undefined : onChange}
              placeholder={typeof placeholder === 'string' ? placeholder : undefined}
              {...rest}
            />
            {!value && placeholder && typeof placeholder !== 'string' && (
              <div className={styles.Placeholder}>
                <div>{placeholder}</div>
              </div>
            )}
          </div>
        )}

        {clearable && (
          <button className={styles.ClearButton} ref={inputRefClearButton} type="button" onClick={onClear} hidden />
        )}
        {withWarning && <span className={styles.WarningIcon} />}
        {error && <div className={styles.TextareaError}>{error}</div>}
        {!error && <ErknmFillInProcessMessage name={rest.name} />}
        {warning && <div className={styles.Warning}>{warning}</div>}
        {inspectionResult && <ErknmInspectionResultMessage inspectionResult={inspectionResult} />}
      </div>
    );
  },
);
